import { inject, Injectable } from '@angular/core';
import { ItemStatus } from '@enums/item-status.enum';
import { DirectusService } from '@services/directus/directus.service';
import { catchError, defer, from, map, Observable, of, shareReplay } from 'rxjs';

interface GroupLookup {
  title: string;
  externalId: string;
}

@Injectable({
  providedIn: 'root'
})
export class GroupsService {
  private readonly directusService = inject(DirectusService);
  private readonly groups$: Observable<GroupLookup[]> = defer(() =>
    from(
      this.directusService.readItems<GroupLookup>('groups', {
        fields: ['title', 'externalId'],
        filter: {
          status: ItemStatus.Published
        },
        limit: -1
      })
    )
  ).pipe(
    map(groups => (Array.isArray(groups) ? groups : [])),
    catchError(() => of([] as GroupLookup[])),
    shareReplay(1)
  );

  /**
   * Resolve SSO external IDs to Directus group titles.
   * Only returns titles for groups that exist in Directus; unmapped IDs are omitted.
   */
  public resolveTitles(externalIds: string[]): Observable<string[]> {
    if (externalIds.length === 0) {
      return of([]);
    }

    const requestedIds = new Set(externalIds);

    return this.groups$.pipe(
      map(groups => {
        const titles = groups
          .filter(group => requestedIds.has(group.externalId) && group.title.trim())
          .map(group => group.title.trim());

        return [...new Set(titles)].sort((a, b) => a.localeCompare(b));
      })
    );
  }
}
